import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpiryBadge } from '@/features/vault/ExpiryField';
import { credentialsApi, type CredentialSummary } from '@/api/credentials';
import { decryptCredential, type CredentialPayload } from '@/crypto/sodium';
import { getRevealTimeout } from '@/stores/preferences';
import { StarIcon } from '@/features/notes/icons';
import TagPill from '@/components/ui/TagPill';
import { type TagResponse } from '@/api/tags';
import { copySecret } from '@/lib/clipboard';
import { formatDateTime } from '@/lib/format';
import { EyeIcon, EyeOffIcon, CopyIcon } from '@/features/vault/listIcons';

interface RowProps {
  summary: CredentialSummary;
  keyPair: { publicKey: Uint8Array; privateKey: Uint8Array };
  secretKey: string | null;
  tagsById: Record<string, TagResponse>;
  onOpen: () => void;
  onTogglePin: () => void;
}

export function CredentialRow({ summary, keyPair, secretKey, tagsById, onOpen, onTogglePin }: RowProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tickRef = useRef<number | null>(null);
  const feedbackRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      if (feedbackRef.current) window.clearTimeout(feedbackRef.current);
    };
  }, []);

  const usernameValue =
    summary.username ??
    (typeof summary.meta?.['username'] === 'string'
      ? (summary.meta['username'] as string)
      : null);

  const decryptSecret = async (): Promise<string | null> => {
    if (!secretKey) return null;
    const detail = await credentialsApi.get(summary.id);
    const plain = (await decryptCredential(
      {
        ciphertext: detail.ciphertext,
        nonce: detail.nonce,
        dek_wrapped: detail.dek_wrapped,
      },
      keyPair,
    )) as CredentialPayload;
    const value = plain[secretKey];
    return typeof value === 'string' ? value : null;
  };

  const flash = (msg: string) => {
    setFeedback(msg);
    if (feedbackRef.current) window.clearTimeout(feedbackRef.current);
    feedbackRef.current = window.setTimeout(() => setFeedback(null), 2500);
  };

  const startCountdown = () => {
    setRemaining(getRevealTimeout());
    if (tickRef.current) window.clearInterval(tickRef.current);
    tickRef.current = window.setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          if (tickRef.current) window.clearInterval(tickRef.current);
          setRevealed(null);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  };

  const handleReveal = async () => {
    if (!secretKey) return;
    if (revealed) {
      if (tickRef.current) window.clearInterval(tickRef.current);
      setRevealed(null);
      setRemaining(0);
      return;
    }
    setBusy(true);
    try {
      const val = await decryptSecret();
      if (val == null) {
        flash(t('vault_ui.list.no_secret'));
      } else {
        setRevealed(val);
        startCountdown();
      }
    } catch (err) {
      flash(
        err instanceof Error
          ? t('vault_ui.list.error_with_message', { message: err.message })
          : t('vault_ui.list.error'),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!secretKey) return;
    setBusy(true);
    try {
      const val = revealed ?? (await decryptSecret());
      if (val == null) {
        flash(t('vault_ui.list.no_secret'));
        return;
      }
      await copySecret(val);
      flash(t('vault_ui.list.copied'));
    } catch (err) {
      flash(
        err instanceof Error
          ? t('vault_ui.list.error_with_message', { message: err.message })
          : t('vault_ui.list.error'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="group border-b border-[var(--border)] last:border-0">
      <td className="py-3 pr-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          className={`transition-opacity ${
            summary.pinned
              ? 'text-amber-500'
              : 'opacity-0 group-hover:opacity-100 text-[var(--text-dim)] hover:text-amber-500'
          }`}
          title={summary.pinned ? t('vault.unpin') : t('vault.pin')}
          aria-label={summary.pinned ? t('vault.unpin') : t('vault.pin')}
        >
          <StarIcon filled={summary.pinned} className="w-3.5 h-3.5" />
        </button>
      </td>
      <td className="py-3">
        <div className="font-medium">
          {summary.title}
          <ExpiryBadge expiresAt={summary.expires_at} />
        </div>
        {summary.tag_ids.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {summary.tag_ids.map((id) => {
              const tag = tagsById[id];
              if (!tag) return null;
              return (
                <TagPill
                  key={id}
                  label={tag.name}
                  color={tag.color ?? 'neutral'}
                  size="sm"
                />
              );
            })}
          </div>
        )}
      </td>
      <td className="py-3">
        <span className="badge bg-[var(--bg-surface)] text-[var(--text-muted)]">{summary.type}</span>
      </td>
      <td className="py-3 font-mono text-xs text-[var(--text-muted)]">
        {usernameValue ?? '—'}
      </td>
      <td className="py-3 font-mono text-xs">
        {secretKey ? (
          <div className="flex items-center gap-2">
            <span className="min-w-[140px] inline-block">
              {revealed ? (
                <span className="text-[var(--text)]">{revealed}</span>
              ) : (
                <span className="text-[var(--text-dim)]">••••••••</span>
              )}
            </span>
            <button
              type="button"
              className="text-[var(--text-muted)] hover:text-brand-600 dark:hover:text-brand-400 disabled:opacity-40"
              onClick={handleReveal}
              disabled={busy}
              title={
                revealed
                  ? t('vault.hide')
                  : t('vault_ui.list.reveal_title', { seconds: getRevealTimeout(), field: secretKey })
              }
              aria-label={t('vault_ui.list.reveal_secret')}
            >
              {revealed ? <EyeOffIcon /> : <EyeIcon />}
            </button>
            <button
              type="button"
              className="text-[var(--text-muted)] hover:text-brand-600 dark:hover:text-brand-400 disabled:opacity-40"
              onClick={handleCopy}
              disabled={busy}
              title={t('vault_ui.list.copy_title', { field: secretKey })}
              aria-label={t('vault_ui.list.copy_secret')}
            >
              <CopyIcon />
            </button>
            {revealed && remaining > 0 && (
              <span className="text-[10px] font-mono text-[var(--text-dim)] tabular-nums">
                {remaining}s
              </span>
            )}
            {feedback && <span className="text-[10px] text-brand-600">{feedback}</span>}
          </div>
        ) : (
          <span className="text-[var(--text-dim)]">—</span>
        )}
      </td>
      <td className="py-3 text-xs text-[var(--text-muted)]">
        {formatDateTime(summary.updated_at)}
      </td>
      <td className="py-3 text-right">
        <button
          type="button"
          onClick={onOpen}
          className="text-brand-700 dark:text-brand-300 hover:underline text-xs"
        >
          {t('vault.details')} →
        </button>
      </td>
    </tr>
  );
}
